import { h, setChildren } from "../core/dom.js";
import { openModal } from "./ui.js";
import { toDisplayWeight, weightLabel } from "../lib/format.js";
import {
  computePlates, KG_PLATES, LB_PLATES, KG_PLATE_OPTIONS, LB_PLATE_OPTIONS, DEFAULT_BAR_KG, DEFAULT_BAR_LB,
} from "../lib/plates.js";

// [background, text] per plate denomination (loose IPF-style colours; small/fractional
// plates fall back to muted greys). kg & lb share the table.
const PLATE_STYLE = {
  45: ["#3b6fd6", "#fff"], 35: ["#e6b800", "#1a1d23"],
  25: ["#d64545", "#fff"], 20: ["#3b6fd6", "#fff"], 15: ["#e6b800", "#1a1d23"],
  10: ["#2f9e44", "#fff"], 5: ["#cbd2da", "#1a1d23"], 2.5: ["#5c636a", "#fff"],
  2: ["#7c8694", "#fff"], 1.5: ["#9aa3b0", "#1a1d23"], 1.25: ["#aab2bd", "#1a1d23"],
  1: ["#b8bfc9", "#1a1d23"], 0.5: ["#cdd3db", "#1a1d23"],
};

const fmt = (n) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));

// Open the plate calculator. weightKg (optional) pre-fills the target from a set.
export function openPlateCalculator({ weightKg } = {}) {
  const unit = weightLabel();
  const isLb = unit === "lbs";
  const options = isLb ? LB_PLATE_OPTIONS : KG_PLATE_OPTIONS;
  const barKey = isLb ? "plate_bar_lb" : "plate_bar_kg";
  const setKey = isLb ? "plate_set_lb" : "plate_set_kg";
  const defaultBar = isLb ? DEFAULT_BAR_LB : DEFAULT_BAR_KG;
  const defaultSet = isLb ? LB_PLATES : KG_PLATES;

  let bar = Number(localStorage.getItem(barKey)) || defaultBar;
  let target = weightKg != null ? Number(toDisplayWeight(weightKg)) : bar;
  let owned = loadOwned(setKey, defaultSet);

  const result = h("div");
  const platesBox = h("div", { class: "chip-row", style: { marginTop: "8px" } });

  function recompute() {
    setChildren(result, ...renderResult(computePlates(target, bar, owned), unit, owned.length));
  }
  function renderPlates() {
    setChildren(platesBox, ...options.map((p) => {
      const on = owned.includes(p);
      return h("button", { class: ["chip", on && "on"], onClick: () => toggle(p) }, fmt(p));
    }));
  }
  function toggle(p) {
    owned = owned.includes(p) ? owned.filter((x) => x !== p) : [...owned, p];
    localStorage.setItem(setKey, JSON.stringify([...owned].sort((a, b) => b - a)));
    renderPlates();
    recompute();
  }

  const targetInput = h("input", {
    class: "input", type: "number", inputmode: "decimal", step: "0.5", value: target,
    oninput: (e) => { target = parseFloat(e.target.value); recompute(); },
  });
  const barInput = h("input", {
    class: "input", type: "number", inputmode: "decimal", step: "0.5", value: bar,
    oninput: (e) => { bar = parseFloat(e.target.value) || 0; localStorage.setItem(barKey, String(bar)); recompute(); },
  });

  openModal({
    title: "Plate calculator",
    content: h("div", null,
      h("div", { class: "hstack", style: { gap: "10px", alignItems: "flex-end" } },
        h("div", { class: "field grow", style: { margin: 0 } }, h("label", null, `Target (${unit})`), targetInput),
        h("div", { class: "field", style: { margin: 0, width: "104px" } }, h("label", null, `Bar (${unit})`), barInput)
      ),
      h("div", { class: "mt-lg" }, result),
      h("div", { class: "mt-lg", style: { borderTop: "1px solid var(--border)", paddingTop: "14px" } },
        h("label", { class: "dim", style: { fontSize: ".8rem", fontWeight: 600 } }, `Available plates (${unit}) — tap to toggle what you own`),
        platesBox
      )
    ),
  });

  renderPlates();
  recompute();
}

function renderResult(r, unit, ownedCount) {
  if (!ownedCount) return [h("p", { class: "dim center" }, "Select the plates you own below.")];
  if (r.belowBar) return [h("p", { class: "dim center" }, "Target is below the bar weight.")];

  const stack = h("div", { class: "plate-stack" },
    r.perSide.length ? r.perSide.map(plateEl) : h("span", { class: "dim" }, "Just the bar — no plates")
  );
  const nodes = [
    h("div", { class: "dim center", style: { fontSize: ".78rem", letterSpacing: ".04em", textTransform: "uppercase", marginBottom: "10px" } }, "Per side"),
    stack,
    h("div", { class: "center mt", style: { fontWeight: 800, fontSize: "1.1rem" } }, `${fmt(r.perSideWeight)} ${unit} / side`),
  ];
  if (!r.achievable && r.remainder > 0) {
    nodes.push(h("p", { class: "center mt", style: { color: "var(--warn)", fontSize: ".85rem" } },
      `Closest match with your plates — ${fmt(r.remainder)} ${unit}/side short of the exact target.`));
  }
  return nodes;
}

function plateEl(p) {
  const [bg, fg] = PLATE_STYLE[p] || ["var(--surface-2)", "var(--text)"];
  return h("span", { class: "plate-pill", style: { background: bg, color: fg } }, fmt(p));
}

function loadOwned(key, def) {
  try {
    const a = JSON.parse(localStorage.getItem(key));
    if (Array.isArray(a) && a.length) return a.filter((n) => typeof n === "number");
  } catch {}
  return def.slice();
}
